/** @format */

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { BOOKING_DETAIL_DTOS, EMP_DETAIL_QUERY, EMP_INFO, HOME_ADDRESS_INFO } from "../constants/query-builder.constants";
import { queryGenerator } from "../functions/booking-utils.functions";
import { createQuery, genQuery, userDetails } from "../functions/gql-query.functions";
import { NsWISConfigurations } from "../models/configurations.model";
import { NsEmpWebExpData } from "../models/emp-web-exp.model";
import { EmpUserService } from "./emp-user.service";

const URL_ENDPOINTS = {
  TIMEZONE: (buid: string) => `${buid}/timezone`,
  BOOKINGS: `fetch/employees`,
  CONFIGS: `configurations`,
  OFFICES: `office`,
  BOOKING_REASONS: `reason/booking`,
  WFH_UPDATE: `wfh/update`,
  WFO_UPDATE: `employees/scan`
};

@Injectable({
  providedIn: "root"
})
export class ApiService {
  constructor(private user: EmpUserService, private http: HttpClient) {}

  getEmpTimezone(): Observable<string> {
    return this.http
      .get<NsEmpWebExpData.ITimezone>(`${this.user.environment.tms}/${URL_ENDPOINTS.TIMEZONE(this.user.employeeInfo.buid)}`, {
        params: {
          empGuid: this.user.employeeInfo.empGuid
        }
      })
      .pipe(map(res => res.zoneId));
  }

  getEmpProfile(query) {
    const headers = new HttpHeaders();
    headers.append("Buid", this.user.employeeInfo.buid);
    return this.http.post<NsEmpWebExpData.IQueryResponse>(`${this.user.environment.empExp}/${URL_ENDPOINTS.BOOKINGS}`, query, { headers });
  }

  getConfigurations(): Observable<NsWISConfigurations.IConfigurations> {
    return this.http.get<NsWISConfigurations.IConfigurations>(
      `${this.user.environment.empExp}/${this.user.employeeInfo.buid}/${this.user.employeeInfo.empGuid}/${URL_ENDPOINTS.CONFIGS}`
    );
  }
  getCommonConfigurations(): Observable<NsWISConfigurations.ICommonConfigurations> {
    const headers = new HttpHeaders({
      "x-user-id": this.user.employeeInfo.empGuid,
      "x-tenant-id": this.user.employeeInfo.buid
    });
    return this.http.get<NsWISConfigurations.ICommonConfigurations>(
      `${this.user.environment.empExp}/${this.user.employeeInfo.buid}/${URL_ENDPOINTS.CONFIGS}/common`,
      { headers: headers }
    );
  }
  getOffices(): Observable<NsEmpWebExpData.IOffice[]> {
    const headers = new HttpHeaders();
    headers.append("Buid", this.user.employeeInfo.buid);
    return this.http.get<NsEmpWebExpData.IOffice[]>(
      `${this.user.environment.tms}/${this.user.employeeInfo.buid}/${URL_ENDPOINTS.OFFICES}/?businessId=${this.user.employeeInfo.buid}`
    );
  }

  genReplayStream<T>(obs: Observable<T>): Observable<T> {
    return obs.pipe(shareReplay(1));
  }

  getEmpShifts(
    start: string,
    end: string,
    emp: NsEmpWebExpData.IEmployee,
    timeZoneId,
    office: NsEmpWebExpData.IOffice
  ): Observable<{ [key: string]: { ScheduleEdit: NsEmpWebExpData.IShiftData[] } }> {
    return this.http
      .get<{ [key: string]: { ScheduleEdit: NsEmpWebExpData.IShiftData[] } }>(
        this.user.environment.empExp + "/" + this.user.employeeInfo.buid + "/getShifts/" + this.user.employeeInfo.empGuid,
        {
          params: {
            startDate: start,
            endDate: end,
            gender: Number(emp.gender) === 1 ? "FEMALE" : "MALE",
            officeName: office.address,
            timeZoneId: encodeURIComponent(timeZoneId)
          }
        }
      )
      .pipe(
        map(res => {
          res[start].ScheduleEdit.forEach(res => {
            const tags = JSON.parse(res.tags);
            const tag = tags.shift_category.find(t => t.name === "TRANSPORT");
            res.transportAvalability = !!tag && tag.visible;
            res.value = res.shiftId;
          });
          return res;
        })
      );
  }
  getEmpShiftsNew(start, end, nextDayEnable, office, triptype): Observable<NsEmpWebExpData.IShiftData[]> {
    const stwId = this.user.getStwId();
    return this.http
      .get<{
        loginShifts?: NsEmpWebExpData.IShiftData[];
        logoutShifts?: NsEmpWebExpData.IShiftData[];
      }>(
        this.user.environment.empExp +
          "/" +
          this.user.employeeInfo.buid +
          "/v2/shifts/" +
          stwId +
          "?curhour=-1&curmin=-1&endDate=" +
          end +
          "&nextDayEnable=" +
          nextDayEnable +
          "&officeName=" +
          office +
          "&shifttype=normal&startDate=" +
          start +
          "&triptype=" +
          triptype
      )
      .pipe(
        map(res => {
          return triptype === "LOGIN" ? res.loginShifts : res.logoutShifts;
        }),
        map(res => {
          return res.map(shift => ({
            ...shift,
            type: shift.shiftType,
            shiftType: shift.type,
            shiftId: shift.scheduleEventId,
            isNextDay: nextDayEnable ? true : false
          }));
        })
      );
  }

  getPremises(startTime, endTime, officeId): Observable<NsEmpWebExpData.IPremise[]> {
    return this.http.get<NsEmpWebExpData.IPremise[]>(this.user.environment.tms + "/" + this.user.employeeInfo.buid + "/bookings/getPremises", {
      params: {
        startTime: Math.floor(startTime / 1000).toString(),
        endTime: Math.floor(endTime / 1000).toString(),
        officeGuid: officeId
      }
    });
    // return this.http.get<any[]>(this.user.environment.empExp + '/' + this.user.employeeInfo.buid + '/bookings/getPremises', { params: { startTime: Math.floor(startTime / 1000), endTime: Math.floor(endTime / 1000), officeGuid: officeId } });
  }

  getEmpPreferences(params?: { startTime: any; officeGuid: any }): Observable<NsEmpWebExpData.IPreference> {
    const rawURL = `${this.user.environment.empExp}/employees/fetchEmployeePreference/${this.user.employeeInfo.buid}/${this.user.employeeInfo.empGuid}`;
    return this.http.get<NsEmpWebExpData.IPreference>(rawURL, { params });
  }

  getSeatAvailablity({ seatId, startTime, endTime, officeGuid }): Observable<any> {
    return this.http.get<NsEmpWebExpData.ISeatData>(
      `${this.user.environment.empExp}/seat/availibity/${this.user.employeeInfo.buid}/${this.user.employeeInfo.empGuid}?seatId=${seatId}&startTime=${startTime}&endTime=${endTime}&officeGuid=${officeGuid}`
    );
  }

  genEmpDetailQuery(start, end, type?, meetingRoomBookingsRequired?) {
    return createQuery(
      genQuery(this.user.employeeInfo.buid, this.user.employeeInfo.empGuid, start, end, type, meetingRoomBookingsRequired),
      userDetails(BOOKING_DETAIL_DTOS)
    );
  }

  genEmpInfoQuery(): string {
    return createQuery(genQuery(this.user.employeeInfo.buid, this.user.employeeInfo.empGuid), userDetails(EMP_INFO, HOME_ADDRESS_INFO));
  }

  createBookings(req) {
    return this.http.post<NsEmpWebExpData.IBookingRequest[]>(
      this.user.environment.empExp + "/" + this.user.employeeInfo.buid + "/bookings/createBulkBookings",
      req,
      {
        params: { requestorGuid: this.user.employeeInfo.empGuid }
      }
    );
  }
  createRecurrenceBookings(req) {
    return this.http.post<NsEmpWebExpData.IRecurrenceBookingResponse>(this.user.environment.empExp + "/route/emp-app/bookings", req);
  }

  updateBookings(req) {
    return this.http.post<NsEmpWebExpData.IBookingRequest[]>(
      this.user.environment.empExp + "/" + this.user.employeeInfo.buid + "/bookings/updateBulkBookings",
      req,
      {
        params: { requestorGuid: this.user.employeeInfo.empGuid }
      }
    );
  }

  cancelBookings(req) {
    return this.http.post<NsEmpWebExpData.IBookingCancelResponse>(this.user.environment.empExp + "/route/emp-app/bookings/cancel", req[0]);
  }

  getProfileRules(): Observable<NsEmpWebExpData.IProfileRules> {
    return this.http.get<NsEmpWebExpData.IProfileRules>(
      this.user.environment.empExp + "/employees/jobTitle/" + `${this.user.employeeInfo.buid}/${this.user.employeeInfo.empGuid}`
    );
  }
  getTeamMates(): Observable<NsEmpWebExpData.ITeammateData> {
    return this.http.get<NsEmpWebExpData.ITeammateData>(
      this.user.environment.empExp + "/" + `${this.user.employeeInfo.buid}/${this.user.employeeInfo.empGuid}` + "/calendar/teammates/wfo"
    );
  }
  wfhUpdate(bookingRequestId: string, obj): Observable<void> {
    return this.http.post<void>(`${this.user.environment.empExp}/${URL_ENDPOINTS.WFH_UPDATE}`, obj, {
      params: {
        bookingRequestId: bookingRequestId,
        buid: this.user.employeeInfo.buid,
        employeeGuid: this.user.employeeInfo.empGuid
      }
    });
  }

  wfoUpdate(req): Observable<NsEmpWebExpData.IAPIResponse> {
    return this.http.post<NsEmpWebExpData.IAPIResponse>(`${this.user.environment.empExp}/${URL_ENDPOINTS.WFO_UPDATE}`, req, {
      params: {
        buid: this.user.employeeInfo.buid
      }
    });
  }

  getBookingReasonList(btype: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.user.environment.empExp}/${URL_ENDPOINTS.BOOKING_REASONS}/${this.user.employeeInfo.buid}/${btype}`);
  }

  validateShift(
    req: NsEmpWebExpData.IShiftValidationRequest[],
    loginShiftId: string,
    logoutShiftId: string,
    zone: string
  ): Observable<NsEmpWebExpData.IShiftValidationResponse> {
    if (!loginShiftId && !logoutShiftId) {
      return of({
        login: { [this.user.employeeInfo.empGuid]: false },
        logout: { [this.user.employeeInfo.empGuid]: false }
      });
    }
    return this.http.post<NsEmpWebExpData.IShiftValidationResponse>(
      `${this.user.environment.tms}/${this.user.employeeInfo.buid}/shiftsValidationInBooking`,
      req,
      {
        params: {
          loginShiftId,
          logoutShiftId,
          pmGuid: this.user.employeeInfo.empGuid,
          timezone: encodeURIComponent(zone)
        }
      }
    );
  }

  getUpdatedSeatPreference(date: number, officeId: string): Observable<NsEmpWebExpData.ISeatData> {
    return this.http
      .get<NsEmpWebExpData.ISeatData>(
        `${this.user.environment.empExp}/employees/preference/${this.user.employeeInfo.buid}/empGuid/${this.user.employeeInfo.empGuid}/${date}/${officeId}`
      )
      .pipe(
        map(seat => {
          if (seat) {
            seat.floorId = seat.premiseId;
            seat.floorName = seat.premiseName;
          }
          return seat;
        })
      );
  }

  getParking(officeGuid: string, startTime: string, endTime: string): Observable<NsEmpWebExpData.IParkingPremise[]> {
    return this.http
      .get<NsEmpWebExpData.IParkingPremise[]>(`${this.user.environment.empExp}/${this.user.employeeInfo.buid}/premises/parking`, {
        params: {
          officeGuid,
          startTime,
          endTime,
          empGuid: this.user.employeeInfo.empGuid
        }
      })
      .pipe(
        map(res => {
          return res.map(prm => {
            prm.value = prm.premiseId;
            prm.label = `${prm.premise} (${prm.availability})`;
            return prm;
          });
        })
      );
  }

  getAllowedDates(start: string, end: string): Observable<string[]> {
    return this.http.get<string[]>(this.user.environment.empExp + "/transport/allowedDates", {
      params: {
        startDate: start,
        endDate: end,
        employeeGuid: this.user.employeeInfo.empGuid,
        buid: this.user.employeeInfo.buid
      }
    });
  }

  getApprovalStatus(bookingType: string, startTime: string): Observable<boolean> {
    return this.http.get<boolean>(this.user.environment.empExp + "/employees/" + this.user.employeeInfo.buid + "/bookingApproval/status", {
      params: {
        bookingType: bookingType,
        guid: this.user.employeeInfo.empGuid,
        startTime: startTime
      }
    });
  }

  getEligibility = () => {
    return this.http.get<NsEmpWebExpData.IEligibilityInfo>(
      `${this.user.environment.empExp}/employees/seat-booking-validity/${this.user.employeeInfo.buid}/${this.user.employeeInfo.empGuid}`
    );
  };

  getVaxStatus() {
    return this.http.get<NsEmpWebExpData.IVaXStatus>(
      `${this.user.environment.empExp}/fetch/vax-status?buid=${this.user.employeeInfo.buid}&empGuid=${this.user.employeeInfo.empGuid}`
    );
  }
  getCustomStatusList(): Observable<NsEmpWebExpData.IDefaultStatusList> {
    return this.http.get<NsEmpWebExpData.IDefaultStatusList>(
      `${this.user.environment.empExp}/${this.user.employeeInfo.buid}/configurations/employeeStatusList`
    );
  }
  captureStatusChange(req: NsEmpWebExpData.IEmployeeCustomStatus): Observable<boolean> {
    return this.http.post<boolean>(`${this.user.environment.empExp}/route/emp-core/employees`, req, {
      params: {
        status: req.statusName
      }
    });
  }
  getLastStatus(): Observable<string> {
    return this.http.get(`${this.user.environment.empExp}/route/emp-core/employees`, { responseType: "text" });
  }
}
